import React, { useState } from "react";
import { LABELS, NO_SEARCH_RESULTS, TABLE_HEADERS } from "@/utils/constants/appConstants/commonConstants";
import { Link, useNavigate } from "react-router-dom";
import { DateFormatter, TimeFormatter, getPersonDisplayName } from "@/utils/functions/helperFunctions";
import StatusBadge from "@/components/badges/StatusBadge";
import { EnvelopeTableSkeletonDesktop, EnvelopeTableSkeletonMobile } from "@/components/skeletons";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import NoDataAvailable from "@/components/cards/NoDataAvailable";
import NoFilterDataCard from "@/components/cards/NoFilterDataCard";
import { MobileDateField } from "@/components/cards/MobileDateField";
import { MobileListChevron } from "@/components/cards/MobileListChevron";
import TransferOwnershipModal from "@/components/modals/TransferOwnershipModal";
import TransferOwnershipButton from "@/components/buttons/TransferOwnershipButton";
import OwnershipSelectCheckbox from "@/components/buttons/OwnershipSelectCheckbox";
import { transferEnvelopeOwnership } from "@/services/envelope.service";
import { invalidateOwnershipTransferListCaches } from "@/utils/listPageCache";
import {
    buildTransferOwnershipPayload,
    type OwnershipSelection,
} from "@/utils/hooks/useOwnershipSelection";
import { successToasterMessage, errorToasterMessage } from "@/utils/functions/toaster";
import { TRANSFER_OWNERSHIP_SUCCESS } from "@/utils/constants/messages";

interface EnvelopeTableProps {
    envelopes: any[];
    setEnvelopes: (envelope: any) => void;
    isLoading: boolean;
    dataLength: number;
    sorting: string;
    allFetched?: boolean;
    setSorting: ({}) => void;
    search: (searchText: string) => void;
    reset: () => void;
    searchItem: string | null;
    title: string;
    dataLoad: boolean;
    resetDataQueries: () => void;
    setPageNo: (pageNo: number) => void;
    id: string;
    /** Optional — omitted on nested pages (e.g. form-details envelopes tab). */
    selection?: OwnershipSelection;
    showTransferActions?: boolean;
}

const EnvelopeTable: React.FC<EnvelopeTableProps> = (props) => {
    const navigate = useNavigate();
    const isCompactLayout = useCompactListLayout();
    const [subjectStatus, setSubjectStatus] = useState(true);
    const [createdAtStatus, setCreatedAtStatus] = useState(true);
    const [sortElement, setSortElement] = React.useState("created_at");
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [selectedEnvelope, setSelectedEnvelope] = useState<any>(null);
    const [transferLoading, setTransferLoading] = useState(false);
    const { selection, showTransferActions = false } = props;
    const isRowSelected = (id: string) => Boolean(selection?.isSelected(id));

    const subjectSort = () => {
        setSubjectStatus((prev) => {
            const next = !prev;
            props.setPageNo(1);
            props.setSorting({
                "meta_data.email_subject": next ? 1 : -1,
            });
            return next;
        });
        setSortElement("subject");
    };

    const createdAtSort = () => {
        setCreatedAtStatus((prev) => {
            const next = !prev;
            props.setPageNo(1);
            props.setSorting({
                created_at: next ? 1 : -1,
            });
            return next;
        });
        setSortElement("created_at");
    };

    const handleOpenTransferModal = (envelope: any) => {
        setTransferLoading(false);
        setSelectedEnvelope(envelope);
        setShowTransferModal(true);
    };

    const handleCloseTransferModal = () => {
        setShowTransferModal(false);
        setSelectedEnvelope(null);
    };

    const handleTransferOwnership = async (selectedUser: any) => {
        if (!selectedUser || !selectedEnvelope) return;

        setTransferLoading(true);
        try {
            const payload = buildTransferOwnershipPayload(selectedUser);
            await transferEnvelopeOwnership(selectedEnvelope.id, payload);
            successToasterMessage(TRANSFER_OWNERSHIP_SUCCESS);
            handleCloseTransferModal();
            selection?.deselect(selectedEnvelope.id);
            invalidateOwnershipTransferListCaches("envelope");
            props.setPageNo(1);
            props.resetDataQueries();
        } catch (error) {
            console.log("Error transferring envelope ownership:", error);
            errorToasterMessage(error);
        } finally {
            setTransferLoading(false);
        }
    };

    const getEnvelopeOwnerName = (envelope: any) =>
        getPersonDisplayName(envelope.owner) || getPersonDisplayName(envelope.created_by);

    const envelopeIds = (props.envelopes ?? []).map((envelope: any) => envelope.id).filter(Boolean);
    const columnCount = showTransferActions ? 6 : 4;
    const isLoadingMore = props.isLoading && !props.dataLoad;

    const renderSelectCheckbox = (envelopeId: string, key: number, suffix = "") =>
        selection ? (
            <OwnershipSelectCheckbox
                id={`${props.id}-select-${key}${suffix}`}
                checked={selection.isSelected(envelopeId)}
                onCheckedChange={() => selection.toggle(envelopeId)}
                onClick={(event) => event.stopPropagation()}
                aria-label={LABELS.TRANSFER_OWNERSHIP}
            />
        ) : null;

    const mobileList =
        props.envelopes && !props.dataLoad ? (
            props.envelopes?.length > 0 ? (
                <>
                    <div className="envelope_table">
                        {props.envelopes?.map((envelope: any, key: number) => (
                            <div
                                className={`envelope_tr gs-mobile-list-card${isRowSelected(envelope.id) ? " gs-table-row-selected" : ""}`}
                                key={envelope.id}
                            >
                                {showTransferActions ? (
                                    <div className="gs-mobile-select-checkbox">
                                        {renderSelectCheckbox(envelope.id, key, "-mb")}
                                    </div>
                                ) : (
                                    <div className="gs-mobile-card-icon">
                                        <em className="icon ni ni-file icons" id={`${props.id}-icon`}></em>
                                    </div>
                                )}
                                <div className="env_meta_data gs-mobile-list-card__content w-100" id={`${props.id}-subject-${key}-mb`}>
                                    {envelope?.meta_data?.email_subject}
                                    <div className="text-soft" id={`${props.id}-recipient-name-${key}-mb`}>
                                        To:{" "}
                                        {envelope.recipients.length === 0
                                            ? "No Recipients Specified"
                                            : envelope.recipients
                                                  .map((v: any) => v.full_name)
                                                  .slice(0, 3)
                                                  .join(", ")}
                                    </div>
                                    <div className="text-dark">
                                        <span className="fw-bold">Owner: </span>
                                        <span className="tb-lead-sub" id={`${props.id}-created-by-${key}-mb`}>
                                            {getEnvelopeOwnerName(envelope)}
                                        </span>
                                    </div>
                                    <div className="text-dark gs-envelope-mobile-status-field">
                                        <span className="fw-bold">{TABLE_HEADERS.STATUS}: </span>
                                        <span className="gs-envelope-mobile-status-value" id={`${props.id}-status-${key}-mb`}>
                                            <StatusBadge envelope={envelope} id={`${key}-${props.id}`} />
                                        </span>
                                    </div>
                                    <MobileDateField
                                        label="Created At: "
                                        value={envelope.created_at}
                                        dateId={`${props.id}-created-at-date-${key}-mb`}
                                        timeId={`${props.id}-created-at-time-${key}-mb`}
                                    />
                                    {showTransferActions ? (
                                        <div className="gs-mobile-transfer-ownership">
                                            <TransferOwnershipButton
                                                id={`envelope-transfer-ownership-btn-${key}-mb`}
                                                iconId={`envelope-transfer-ownership-icon-${key}-mb`}
                                                onClick={() => handleOpenTransferModal(envelope)}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                                <MobileListChevron
                                    id={`${props.id}-redirect-${key}-mb`}
                                    onClick={() => navigate(`/envelope/${envelope.id}`)}
                                />
                            </div>
                        ))}
                    </div>
                    {isLoadingMore ? (
                        <EnvelopeTableSkeletonMobile
                            rowCount={2}
                            showActionColumn={showTransferActions}
                        />
                    ) : null}
                </>
            ) : props.searchItem ? (
                <NoDataAvailable>
                    <p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p>
                </NoDataAvailable>
            ) : (
                <NoDataAvailable>
                    <NoFilterDataCard id={props.id} loadAll={props.resetDataQueries} />
                </NoDataAvailable>
            )
        ) : (
            <EnvelopeTableSkeletonMobile showActionColumn={showTransferActions} />
        );

    return (
        <>
            {isCompactLayout ? (
                mobileList
            ) : (
                <table
                    className={`nk-tb-list nk-tb-ulist table-weight gs-envelope-table${showTransferActions ? " gs-envelope-table--with-actions" : ""}`}
                    style={{ tableLayout: "fixed", width: "100%" }}
                >
                    <colgroup>
                        {showTransferActions ? <col className="gs-envelope-col-select" /> : null}
                        <col className="gs-envelope-col-subject" />
                        <col className="gs-envelope-col-owner" />
                        <col className="gs-envelope-col-status" />
                        <col className="gs-envelope-col-created" />
                        {showTransferActions ? <col className="gs-envelope-col-actions" /> : null}
                    </colgroup>
                    <thead className="">
                        <tr className="nk-tb-item nk-tb-head">
                            {showTransferActions && selection ? (
                                <th className="nk-tb-col gs-envelope-col-select align-middle">
                                    <OwnershipSelectCheckbox
                                        id={`${props.id}-select-all`}
                                        checked={
                                            selection.areAllSelected(envelopeIds)
                                                ? true
                                                : selection.areSomeSelected(envelopeIds)
                                                  ? "indeterminate"
                                                  : false
                                        }
                                        onCheckedChange={() => selection.toggleAll(envelopeIds)}
                                        aria-label="Select all envelopes"
                                    />
                                </th>
                            ) : null}
                            <th className="nk-tb-col gs-envelope-col-subject" onClick={subjectSort}>
                                <span id={`${props.id}-subject-th`}>{TABLE_HEADERS.SUBJECT}</span>
                                <em
                                    id={`${props.id}-subject-th-sort-icon`}
                                    className={`ml-1  icon ni ${sortElement === "subject" ? (subjectStatus === true ? "ni-upword-alt-fill" : "ni-downward-alt-fill") : ""}`}
                                ></em>
                            </th>
                            <th className="nk-tb-col gs-envelope-col-owner">
                                <span id={`${props.id}-owner-th`}>{TABLE_HEADERS.OWNER}</span>
                            </th>
                            <th className="nk-tb-col gs-envelope-col-status">
                                <span id={`${props.id}-status-th`}>{TABLE_HEADERS.STATUS}</span>
                            </th>
                            <th className="nk-tb-col gs-envelope-col-created" onClick={createdAtSort}>
                                <span id={`${props.id}-created-at-th`}>{TABLE_HEADERS.CREATED_AT}</span>
                                <em
                                    id={`${props.id}-created-at-th-sort-icon`}
                                    className={`ml-1  icon ni ${sortElement === "created_at" ? (createdAtStatus === true ? "ni-upword-alt-fill" : "ni-downward-alt-fill") : ""}`}
                                ></em>
                            </th>
                            {showTransferActions ? (
                                <th className="nk-tb-col gs-envelope-col-actions">
                                    <span id={`${props.id}-actions-th`}>{TABLE_HEADERS.ACTIONS}</span>
                                </th>
                            ) : null}
                        </tr>
                    </thead>
                    {props.envelopes && !props.dataLoad ? (
                        props.envelopes?.length > 0 ? (
                            <tbody>
                                {props.envelopes.map((envelope: any, key: number) => (
                                    <tr
                                        className={`nk-tb-item${isRowSelected(envelope.id) ? " gs-table-row-selected" : ""}`}
                                        key={envelope.id ?? key}
                                    >
                                        {showTransferActions ? (
                                            <td className="nk-tb-col gs-envelope-col-select align-middle">
                                                {renderSelectCheckbox(envelope.id, key)}
                                            </td>
                                        ) : null}
                                        <td className="nk-tb-col gs-envelope-col-subject">
                                            <Link to={`/envelope/${envelope?.id}`} className="user-card">
                                                <div className="nk-tnx-type w-100">
                                                    <div className="bg-fail-dim icon mr-2">
                                                        <em className="icon ni ni-file icons" id={`${props.id}-icon-${key}`}></em>
                                                    </div>
                                                    <div
                                                        className="data"
                                                        style={{
                                                            marginTop: 4,
                                                            maxWidth: "100%",
                                                            overflow: "hidden",
                                                            whiteSpace: "nowrap",
                                                            textOverflow: "ellipsis",
                                                        }}
                                                    >
                                                        <div className="info title-overflow">
                                                            <div id={`${props.id}-subject-${key}`} className="text-dark name">
                                                                {envelope.meta_data?.email_subject
                                                                    ? envelope.meta_data?.email_subject
                                                                    : "Untitled"}
                                                            </div>
                                                            <div className="meta" style={{ marginTop: 4 }}>
                                                                <span className="version">
                                                                    <span className="text-soft" id={`${props.id}-recipient-name-${key}`}>
                                                                        To:{" "}
                                                                        {envelope.recipients.length === 0
                                                                            ? "No Recipients Specified"
                                                                            : envelope.recipients
                                                                                  .map((v: any) => v.full_name)
                                                                                  .slice(0, 3)
                                                                                  .join(", ")}
                                                                    </span>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Link>
                                        </td>
                                        <td className="nk-tb-col gs-envelope-col-owner">
                                            <span className="tb-amount" id={`${props.id}-owner-by-${key}`}>
                                                {getEnvelopeOwnerName(envelope)}
                                            </span>
                                        </td>
                                        <td className="nk-tb-col gs-envelope-col-status">
                                            <StatusBadge envelope={envelope} id={`${key}-${props.id}`} />
                                        </td>
                                        <td className="nk-tb-col gs-envelope-col-created">
                                            <span className="text-dark" id={`${props.id}-created-at-date-${key}`}>
                                                {DateFormatter(envelope.created_at)}
                                            </span>
                                            <div>
                                                <div className="meta mt-1">
                                                    <span className="version">
                                                        <span className="text-soft" id={`${props.id}-created-at-time-${key}`}>
                                                            {TimeFormatter(envelope.created_at)}
                                                        </span>
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        {showTransferActions ? (
                                            <td className="nk-tb-col gs-envelope-col-actions">
                                                <TransferOwnershipButton
                                                    id={`envelope-transfer-ownership-btn-${key}`}
                                                    iconId={`envelope-transfer-ownership-icon-${key}`}
                                                    onClick={() => handleOpenTransferModal(envelope)}
                                                />
                                            </td>
                                        ) : null}
                                    </tr>
                                ))}
                                {isLoadingMore ? (
                                    <EnvelopeTableSkeletonDesktop
                                        rowCount={2}
                                        showActionColumn={showTransferActions}
                                    />
                                ) : null}
                            </tbody>
                        ) : props.searchItem ? (
                            <tbody>
                                <tr>
                                    <td colSpan={columnCount}>
                                        <NoDataAvailable>
                                            <p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p>
                                        </NoDataAvailable>
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody>
                                <tr>
                                    <td colSpan={columnCount}>
                                        <NoDataAvailable>
                                            <NoFilterDataCard id={props.id} loadAll={props.resetDataQueries} />
                                        </NoDataAvailable>
                                    </td>
                                </tr>
                            </tbody>
                        )
                    ) : (
                        <tbody>
                            <EnvelopeTableSkeletonDesktop showActionColumn={showTransferActions} />
                        </tbody>
                    )}
                </table>
            )}

            {selectedEnvelope && (
                <TransferOwnershipModal
                    show={showTransferModal}
                    onHide={handleCloseTransferModal}
                    onTransferOwnership={handleTransferOwnership}
                    loading={transferLoading}
                    currentOwnerId={selectedEnvelope?.owner?.id}
                    title={LABELS.TRANSFER_ENVELOPE_OWNERSHIP}
                    description={LABELS.TRANSFER_ENVELOPE_OWNERSHIP_DESCRIPTION}
                />
            )}
        </>
    );
};

export default EnvelopeTable;
